import React, { useState, useCallback } from "react";
import { UploadCloud } from "lucide-react";
import { useDropzone } from "react-dropzone";
import ImageCropper from "./ImageCropper";
import getCroppedImg from "@/utils/image";
import { Area } from "react-easy-crop";
import apiClient from "@/lib/api-client";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface AvatarUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: (url: string) => void;
}

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const { setUser, user } = useAuthStore();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleUpload = async () => {
    if (!image || !croppedAreaPixels) return;

    try {
      setUploading(true);
      const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels);
      if (!croppedImageBlob) throw new Error("Failed to crop image");

      const formData = new FormData();
      formData.append("avatar", croppedImageBlob, "avatar.jpg");

      const response = await apiClient.post("/users/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { url } = response.data.data;

      // Update local auth state
      if (user) {
        setUser({ ...user, avatar: url });
      }

      toast.success("Avatar updated successfully");
      if (onUploadSuccess) onUploadSuccess(url);
      onClose();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload avatar");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setImage(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update profile picture</DialogTitle>
          <DialogDescription>Upload an image, then crop it to fit.</DialogDescription>
        </DialogHeader>

        {!image ? (
          <div
            {...getRootProps()}
            className={cn(
              "flex h-[250px] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed px-4 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isDragActive
                ? "border-foreground/50 bg-accent"
                : "border-hairline-strong hover:border-foreground/40 hover:bg-accent",
            )}
          >
            <input {...getInputProps()} aria-label="Choose image" />
            <span
              className="mb-3 flex size-10 items-center justify-center rounded-md border border-border bg-card text-muted-foreground"
              aria-hidden
            >
              <UploadCloud className="size-5" />
            </span>
            <p className="text-sm font-medium text-foreground">Click or drag image to upload</p>
            <p className="mt-1 text-caption text-muted-foreground">PNG, JPG or JPEG (Max 5MB)</p>
          </div>
        ) : (
          <ImageCropper image={image} onCropComplete={onCropComplete} />
        )}

        <DialogFooter className="sm:justify-between">
          <div>
            {image && (
              <Button variant="ghost" onClick={() => setImage(null)} className="w-full sm:w-auto">
                Change image
              </Button>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!image} loading={uploading} className="min-w-[100px]">
              Save photo
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarUploadModal;
